import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../../molecules/PageHeader';
import SelectFilter from '../../organisms/SelectFilter';
import { toast } from 'react-toastify';

const API_KEY = process.env.REACT_APP_API_KEY || '';
const BASE_URL = `${process.env.REACT_APP_RECOMMENDATION_ENGINE_API_URL}/rules`;

interface PLDRuleForm {
    name: string;
    description: string;
    provider: string;
    context: string;
    person_type: string;
    field_path: string;
    field_type: string;
    comparison_type: string;
    comparison_value: string;
    recommendation: string;
    pld_flag: boolean;
    credit_flag: boolean;
    active: boolean;
}

const RecommendationEnginePageForm: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState<PLDRuleForm>({
        name: '',
        description: '',
        provider: 'INOVAMIND',
        context: 'PLD',
        person_type: 'PF',
        field_path: '',
        field_type: 'BOOLEAN',
        comparison_type: 'EQUALS',
        comparison_value: '',
        recommendation: 'REJECTED',
        pld_flag: true,
        credit_flag: false,
        active: true
    });
    
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Providers estáticos (padrão BFF desacoplado)
    const providers = [
        { value: 'INOVAMIND', label: 'INOVAMIND' },
        { value: 'ESCAVADOR', label: 'ESCAVADOR' },
        { value: 'QUOD', label: 'QUOD' },
        { value: 'SERASA', label: 'SERASA' }
    ];

    const fieldTypes = [
        { value: 'BOOLEAN', label: 'Verdadeiro/Falso (Sim/Não)' },
        { value: 'TEXT', label: 'Texto' },
        { value: 'NUMBER', label: 'Número' },
        { value: 'ARRAY', label: 'Lista de valores' }
    ];

    const comparisonTypes = [
        { value: 'EQUALS', label: 'Igual a' },
        { value: 'NOT_EQUALS', label: 'Diferente de' },
        { value: 'CONTAINS', label: 'Contém' },
        { value: 'NOT_CONTAINS', label: 'Não contém' },
        { value: 'GREATER_THAN', label: 'Maior que' },
        { value: 'LESS_THAN', label: 'Menor que' },
        { value: 'GREATER_EQUAL', label: 'Maior ou igual a' },
        { value: 'LESS_EQUAL', label: 'Menor ou igual a' }
    ];

    const recommendations = [
        { value: 'REJECTED', label: 'Reprovado' },
        { value: 'PENDING', label: 'Pendente' }
    ];

    // Campos comuns por provider
    const commonFields = {
        INOVAMIND: [
            { value: 'name', label: 'Nome' },
            { value: 'analysis_status', label: 'Status da Análise' },
            { value: 'bacen_qgi_cpf', label: 'BACEN QGI - Inabilitados Banco Central' },
            { value: 'pep_search_v2', label: 'PEP - Pessoa Exposta Politicamente' },
            { value: 'sanctions_list', label: 'Sanções OFAC (EUA)' },
            { value: 'eu_sanction_list', label: 'Sanções União Europeia' },
            { value: 'fr_sanction_list', label: 'Sanções França' },
            { value: 'uk_sanction_list', label: 'Sanções Reino Unido' },
            { value: 'onu_sanction_lists', label: 'Sanções ONU' },
            { value: 'us_nonproliferation_sanction_list', label: 'Sanções EUA Não-Proliferação' },
            { value: 'red_notice_interpol', label: 'Interpol - Difusão Vermelha' },
            { value: 'cvm_sanctioning_administrative_process', label: 'CVM - Processo Administrativo' },
            { value: 'ceaf_infos_government_base', label: 'CEAF - Base Governamental' },
            { value: 'ceis_infos_government_base_cnpj', label: 'CEIS - Inidôneos e Suspensos' },
            { value: 'administrative_improbity_cpf', label: 'CNJ - Improbidade Administrativa' },
            { value: 'ibama_negative_certificate_cnpj', label: 'IBAMA - Certificado Negativo CNPJ' },
            { value: 'ibama_assessment_cnpj', label: 'IBAMA - Multas CNPJ' },
            { value: 'ibama_embargo_cnpj', label: 'IBAMA - Embargos CNPJ' },
            { value: 'court_warrant_bnmp_cpf', label: 'BNMP - Mandados de Prisão' },
            { value: 'criminal_record_federal_police', label: 'Antecedentes Criminais PF' },
            { value: 'work_slave_cpf', label: 'Trabalho Escravo CPF' }
        ],
        ESCAVADOR: [
            { value: 'status', label: 'Status da Consulta' },
            { value: 'sigla_assunto', label: 'Sigla do Assunto do Processo' },
            { value: 'assunto', label: 'Assunto do Processo' },
            { value: 'classe', label: 'Classe do Processo' },
            { value: 'situacao', label: 'Situação do Processo' },
            { value: 'segredo_justica', label: 'Segredo de Justiça' },
            { value: 'valor_causa_valor', label: 'Valor da Causa' },
            { value: 'tribunal', label: 'Tribunal' },
            { value: 'arquivado', label: 'Processo Arquivado' }
        ],
        QUOD: [
            { value: 'email', label: 'Email' },
            { value: 'phoneNumber', label: 'Telefone' },
            { value: 'score', label: 'Score PF' },
            { value: 'scorePJ', label: 'Score PJ' },
            { value: 'pendenciesControlCred', label: 'Pendências Controle de Crédito' },
            { value: 'valorProtestadosTotal', label: 'Valor Total Protestado' },
            { value: 'totalProtests', label: 'Total de Protestos' },
            { value: 'partnerRestriction', label: 'Sócio com Restrição' },
            { value: 'partnerships', label: 'Sociedades' },
            { value: 'CompanyStatus', label: 'Status da Empresa na RF' },
            { value: 'MainActivity', label: 'Atividade Principal RF' }
        ],
        SERASA: [
            { value: 'documentNumber', label: 'Número do Documento' },
            { value: 'statusRegistration', label: 'Status do Registro' },
            { value: 'statusCodeDescription', label: 'Descrição do Status' },
            { value: 'companyName', label: 'Nome da Empresa' },
            { value: 'city', label: 'Cidade' },
            { value: 'state', label: 'Estado (UF)' },
            { value: 'phoneNumber', label: 'Telefone' },
            { value: 'companyFoundation', label: 'Data de Fundação' },
            { value: 'pefinCount', label: 'PEFIN - Quantidade' },
            { value: 'pefinBalance', label: 'PEFIN - Saldo' },
            { value: 'pefin', label: 'PEFIN - Dados' },
            { value: 'refinCount', label: 'REFIN - Quantidade' },
            { value: 'refinBalance', label: 'REFIN - Saldo' },
            { value: 'refin', label: 'REFIN - Dados' },
            { value: 'collectionRecordsCount', label: 'Dívidas Vencidas - Quantidade' },
            { value: 'collectionRecordsBalance', label: 'Dívidas Vencidas - Saldo' },
            { value: 'collectionRecords', label: 'Registros de Cobrança' },
            { value: 'notaryCount', label: 'Protestos - Quantidade' },
            { value: 'notaryBalance', label: 'Protestos - Saldo' },
            { value: 'notary', label: 'Dados de Protestos' },
            { value: 'judgementFilingsCount', label: 'Ações Judiciais - Quantidade' },
            { value: 'judgementFilingsBalance', label: 'Ações Judiciais - Saldo' },
            { value: 'judgementFilings', label: 'Dados de Ações Judiciais' },
            { value: 'bankruptsCount', label: 'Falências - Quantidade' },
            { value: 'bankruptsBalance', label: 'Falências - Saldo' },
            { value: 'bankrupts', label: 'Dados de Falências' },
            { value: 'results', label: 'Consultas Serasa' },
            { value: 'negativeSummary', label: 'Resumo Negativo' },
            { value: 'socialCapitalValue', label: 'Capital Social' },
            { value: 'partnerRestrict', label: 'Sócio com Restrição' },
            { value: 'partnersList', label: 'Lista de Sócios' },
            { value: 'directorsList', label: 'Lista de Diretores' },
            { value: 'score', label: 'Score Serasa' },
            { value: 'defaultRate', label: 'Taxa de Inadimplência' }
        ]
    };

    useEffect(() => {
        if (isEdit) {
            loadRule();
        }
    }, [id, isEdit]);

    const loadRule = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${BASE_URL}/${id}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            
            if (response.ok) {
                const data = await response.json();
                setFormData(data.rule);
            } else {
                const errorData = await response.json();
                toast.error(errorData.message || 'Erro ao carregar regra');
                navigate('/recommendation-engine/rules/0/20/name/ASC');
            }
        } catch (error) {
            console.error('Erro ao carregar regra:', error);
            toast.error('Erro de conexão ao carregar regra');
            navigate('/recommendation-engine/rules/0/20/name/ASC');
        } finally {
            setLoading(false);
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Nome é obrigatório';
        }

        if (!formData.field_path.trim()) {
            newErrors.field_path = 'Campo da API é obrigatório';
        }

        if (!formData.comparison_value.trim()) {
            newErrors.comparison_value = 'Valor de comparação é obrigatório';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);
        try {
            const url = isEdit ? `${BASE_URL}/${id}` : BASE_URL;
            const method = isEdit ? 'PUT' : 'POST';
            
            const response = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    ...formData,
                    created_by: 'USER', // TODO: pegar do contexto do usuário
                    updated_by: 'USER'  // TODO: pegar do contexto do usuário
                })
            });
            
            if (response.ok) {
                toast.success(isEdit ? 'Regra atualizada com sucesso!' : 'Regra criada com sucesso!');
                navigate('/recommendation-engine/rules/0/20/name/ASC');
            } else {
                toast.error('Erro ao salvar regra');
            }
        } catch (error) {
            toast.error('Erro ao salvar regra');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (field: keyof PLDRuleForm, value: string | boolean) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    const getFieldsForProvider = () => {
        return commonFields[formData.provider as keyof typeof commonFields] || [];
    };

    const getComparisonPlaceholder = () => {
        switch (formData.field_type) {
            case 'BOOLEAN':
                return 'true ou false';
            case 'NUMBER':
                return 'Ex: 200000';
            case 'TEXT':
                return 'Ex: ATIVO, CLP, culpado';
            case 'ARRAY':
                return 'Ex: CLP, DUP, RJF';
            default:
                return 'Valor para comparação';
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-slate-800">
            <PageHeader
                title={isEdit ? 'Editar Regra PLD' : 'Nova Regra PLD'}
                breadcrumb={[
                    { label: 'Motor de Recomendação', url: '/recommendation-engine/rules/0/20/name/ASC' },
                    { label: 'Regras', url: '/recommendation-engine/rules/0/20/name/ASC' },
                    { label: isEdit ? 'Editar Regra' : 'Nova Regra', url: '#' }
                ]}
                apiVersion="Recommendation Engine - 1.0.0"
            >
                <div></div>
            </PageHeader>

            <div className="p-6">
                <form onSubmit={handleSubmit}>
                    <div className="bg-white dark:bg-slate-700 rounded-lg shadow">
                        <div className="p-6">
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                                {/* Informações Básicas */}
                                <div className="space-y-6">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b pb-2">
                                        Informações da Regra
                                    </h3>
                                    
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Nome da Regra *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => handleInputChange('name', e.target.value)}
                                            className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 ${
                                                errors.name 
                                                    ? 'border-red-500 focus:border-red-500' 
                                                    : 'border-gray-300 dark:border-slate-500 focus:border-blue-500'
                                            }`}
                                            placeholder="Ex: INOVAMIND - Sanções ONU"
                                        />
                                        {errors.name && (
                                            <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Descrição da Regra *
                                        </label>
                                        <textarea
                                            value={formData.description}
                                            onChange={(e) => handleInputChange('description', e.target.value)}
                                            className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 ${
                                                errors.description 
                                                    ? 'border-red-500 focus:border-red-500' 
                                                    : 'border-gray-300 dark:border-slate-500 focus:border-blue-500'
                                            }`}
                                            placeholder="Ex: Verifica se a pessoa possui sanções internacionais da ONU"
                                            rows={3}
                                        />
                                        {errors.description && (
                                            <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                                        )}
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <SelectFilter
                                                id="provider"
                                                label="Provedor *"
                                                value={formData.provider}
                                                onChange={(value) => handleInputChange('provider', value)}
                                                options={providers}
                                                defaultVoidOption={true}
                                                defaultLabel="Selecione um provedor..."
                                            />
                                        </div>

                                        <div>
                                            <SelectFilter
                                                id="context"
                                                label="Contexto *"
                                                value={formData.context}
                                                onChange={(value) => handleInputChange('context', value)}
                                                options={[
                                                    { value: 'PLD', label: 'PLD' },
                                                    { value: 'CREDIT', label: 'Crédito' }
                                                ]}
                                                defaultVoidOption={true}
                                                defaultLabel="Selecione um contexto..."
                                            />
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <SelectFilter
                                                id="person_type"
                                                label="Tipo de Pessoa *"
                                                value={formData.person_type}
                                                onChange={(value) => handleInputChange('person_type', value)}
                                                options={[
                                                    { value: 'PF', label: 'Pessoa Física (PF)' },
                                                    { value: 'PJ', label: 'Pessoa Jurídica (PJ)' }
                                                ]}
                                                defaultVoidOption={false}
                                            />
                                        </div>

                                        <div>
                                            <SelectFilter
                                                id="field_path"
                                                label="Campo da API *"
                                                value={formData.field_path}
                                                onChange={(value) => handleInputChange('field_path', value)}
                                                options={getFieldsForProvider()}
                                                defaultVoidOption={true}
                                                defaultLabel="Selecione um campo..."
                                            />
                                        </div>
                                    </div>
                                    
                                    <div>
                                        <p className="mt-1 text-xs text-gray-500">
                                            Campo que vem na resposta da API do provedor
                                        </p>
                                    </div>
                                </div>

                                {/* Configuração da Regra */}
                                <div className="space-y-6">
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 border-b pb-2">
                                        Configuração da Regra
                                    </h3>

                                    <div>
                                        <SelectFilter
                                            id="field_type"
                                            label="Tipo do Campo *"
                                            value={formData.field_type}
                                            onChange={(value) => handleInputChange('field_type', value)}
                                            options={fieldTypes}
                                            defaultVoidOption={true}
                                            defaultLabel="Selecione um tipo..."
                                        />
                                    </div>

                                    <div>
                                        <SelectFilter
                                            id="comparison_type"
                                            label="Tipo de Comparação *"
                                            value={formData.comparison_type}
                                            onChange={(value) => handleInputChange('comparison_type', value)}
                                            options={comparisonTypes}
                                            defaultVoidOption={true}
                                            defaultLabel="Selecione um tipo..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                            Valor de Comparação *
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.comparison_value}
                                            onChange={(e) => handleInputChange('comparison_value', e.target.value)}
                                            className={`w-full px-3 py-2 border rounded-md bg-white dark:bg-slate-600 text-gray-900 dark:text-gray-100 ${
                                                errors.comparison_value 
                                                    ? 'border-red-500 focus:border-red-500' 
                                                    : 'border-gray-300 dark:border-slate-500 focus:border-blue-500'
                                            }`}
                                            placeholder={getComparisonPlaceholder()}
                                        />
                                        {errors.comparison_value && (
                                            <p className="mt-1 text-sm text-red-600">{errors.comparison_value}</p>
                                        )}
                                    </div>

                                    <div>
                                        <SelectFilter
                                            id="recommendation"
                                            label="Recomendação quando acionada *"
                                            value={formData.recommendation}
                                            onChange={(value) => handleInputChange('recommendation', value)}
                                            options={recommendations}
                                            defaultVoidOption={true}
                                            defaultLabel="Selecione uma recomendação..."
                                        />
                                    </div>

                                    <div className="space-y-4">
                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="credit_flag"
                                                checked={formData.credit_flag}
                                                onChange={(e) => handleInputChange('credit_flag', e.target.checked)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="credit_flag" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                Sinalizar para Crédito
                                            </label>
                                        </div>

                                        <div className="flex items-center">
                                            <input
                                                type="checkbox"
                                                id="active"
                                                checked={formData.active}
                                                onChange={(e) => handleInputChange('active', e.target.checked)}
                                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                            />
                                            <label htmlFor="active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                                Regra Ativa
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="px-6 py-4 bg-gray-50 dark:bg-slate-600 border-t border-gray-200 dark:border-slate-500 rounded-b-lg">
                            <div className="flex justify-end space-x-3">
                                <button
                                    type="button"
                                    onClick={() => navigate('/recommendation-engine/rules/0/20/name/ASC')}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-slate-700 border border-gray-300 dark:border-slate-500 rounded-md hover:bg-gray-50 dark:hover:bg-slate-600"
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {loading ? 'Salvando...' : (isEdit ? 'Atualizar Regra' : 'Criar Regra')}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RecommendationEnginePageForm;
